import React, { useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Grid, IconButton, Typography } from '@mui/material';
import { Edit as EditIcon, LockReset as LockResetIcon } from '@mui/icons-material';
import {
  ExternalLinkButton,
  GenderAgeDisplay,
  InfoFieldItem,
  MaskedEmailDisplay,
  MemoEditDialog,
  PasswordResetDialog,
  SectionWithPaper,
  TimeDisplay
} from '../common';
import { updatePatientMemo } from '../../api/patients';
import { FAMILY_RELATIONSHIP_TYPES, Patient, ROLE_COLORS, ROLE_TYPES, WORK_TYPES } from '../../types/patient';
import { getEmploymentLabel, getPrefectureLabel } from '../../types/common';
import { getExternalPatientUrl } from '../../utils/urlUtils';

interface ProfileSectionProps {
  patient: Patient;
  cancerHistoriesContent?: React.ReactNode; // がん罹患歴セクションの内容をプロップとして受け取る
  onPatientUpdate?: () => void; // 患者情報更新時のコールバック
}

// 未入力表示用のスタイル
const getUnsetStyle = (value: string) => {
  return value === '[未入力]' ? {
    color: 'text.disabled',
    fontWeight: 'normal',
    fontStyle: 'italic',
  } : {
    fontWeight: 'bold',
  };
};
// パスワードリセットの有効期限を計算する関数
const calculatePasswordResetExpiry = (resetSentAt: string): { isExpired: boolean; remainingTime: string } => {
  const sentTime = new Date(resetSentAt).getTime();
  const expiryTime = sentTime + (6 * 60 * 60 * 1000); // 6時間後
  const now = new Date().getTime();
  if (now > expiryTime) {
    return { isExpired: true, remainingTime: '有効期限切れ' };
  }
  const remaining = expiryTime - now;
  const hours = Math.floor(remaining / (60 * 60 * 1000));
  const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
  if (hours > 0) {
    return { isExpired: false, remainingTime: `残り ${hours}時間${minutes}分` };
  } else {
    return { isExpired: false, remainingTime: `残り ${minutes}分` };
  }
};
// ロール名を日本語に変換（家族の場合は関係性も表示）
const getRoleLabel = (roleType: string, relationshipType?: string | null) => {
  const baseLabel = ROLE_TYPES[roleType as keyof typeof ROLE_TYPES] || roleType;
  // 家族の場合は関係性も表示
  if (roleType === 'FAMILY' && relationshipType) {
    const relationshipLabel = FAMILY_RELATIONSHIP_TYPES[relationshipType as keyof typeof FAMILY_RELATIONSHIP_TYPES];
    if (relationshipLabel) {
      return `${baseLabel}（${relationshipLabel}）`;
    }
  }
  return baseLabel;
};
const ProfileSection: React.FC<ProfileSectionProps> = ({ patient, cancerHistoriesContent, onPatientUpdate }) => {
  const [ isEditDialogOpen, setIsEditDialogOpen ] = useState(false);
  const [ isPasswordResetDialogOpen, setIsPasswordResetDialogOpen ] = useState(false);
  // パスワードリセットの有効期限を計算
  const passwordResetExpiry = useMemo(() => {
    if (!patient.reset_password_sent_at) return null;
    return calculatePasswordResetExpiry(patient.reset_password_sent_at);
  }, [ patient.reset_password_sent_at ]);
  const handleEditClick = () => {
    setIsEditDialogOpen(true);
  };
  const handlePasswordResetClick = () => {
    setIsPasswordResetDialogOpen(true);
  };
  const handleMemoSubmit = async(memo: string) => {
    await updatePatientMemo(patient.id, memo);
    // 患者詳細画面をリロード
    if (onPatientUpdate) {
      onPatientUpdate();
    } else {
      // フォールバック: ページ全体をリロード
      window.location.reload();
    }
  };
  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Grid container spacing={3}>
          {/* ヘッダー情報 */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              {/* 名前とニックネーム（上下に配置） */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h5" component="h2" sx={{ lineHeight: 1.3, fontSize: '1.8rem', fontWeight: 'bold' }}>
                      {patient.name}
                    </Typography>
                    <GenderAgeDisplay
                      genderType={patient.gender_type}
                      birthday={patient.birthday}
                      showTooltip
                      tooltipPlacement="top"
                      fontSize="1.2rem"
                      color="text.secondary"
                      fontWeight="normal"
                      marginLeft={1.5}
                    />
                  </Box>

                  <ExternalLinkButton
                    href={getExternalPatientUrl(patient.id)}
                    tooltip="患者ページをアプリで開く"
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.95rem', mr: 0.5 }}>
                    ニックネーム:
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                    {patient.nickname}
                  </Typography>
                </Box>
              </Box>

              {/* 右上の情報 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                {/* 登録日時 - 人間が読みやすい形式 */}
                <TimeDisplay
                  timestamp={patient.created_at}
                  prefix="登録"
                  tooltipPrefix="登録日時"
                  tooltipPlacement="left"
                  fontSize="0.9rem"
                />

                {/* 退会日時 - 退会済みの場合のみ表示 */}
                {patient.resigned_at && (
                  <TimeDisplay
                    timestamp={patient.resigned_at}
                    prefix="退会"
                    tooltipPrefix="退会日時"
                    tooltipPlacement="left"
                    color="error.main"
                    iconColor="error"
                    fontSize="0.9rem"
                    fontWeight="medium"
                  />
                )}

                {/* ロール (右端・大きめ) */}
                <Chip
                  label={getRoleLabel(patient.role_type, patient.family_relationship_type)}
                  size="medium"
                  sx={{
                    backgroundColor: ROLE_COLORS[patient.role_type as keyof typeof ROLE_COLORS] || '#757575',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    height: '36px',
                    px: 1
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>基本情報</Typography>
              <Grid container spacing={2}>
                {/* 1行目 */}
                <Grid item xs={12} md={6}>
                  <InfoFieldItem label="就労状況" labelWidth="100px">
                    <Typography component="span" sx={{ fontWeight: 'bold' }}>
                      {getEmploymentLabel(patient.employment_type)}
                    </Typography>
                  </InfoFieldItem>
                </Grid>

                {/* 2行目 */}
                <Grid item xs={12} md={6}>
                  <InfoFieldItem label="都道府県" labelWidth="100px">
                    <Typography component="span" sx={{ fontWeight: 'bold' }}>
                      {getPrefectureLabel(patient.prefecture_type)}
                    </Typography>
                  </InfoFieldItem>
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoFieldItem label="就労形態" labelWidth="100px">
                    <Typography
                      component="span"
                      sx={getUnsetStyle(!patient.work_type ? '[未入力]' : WORK_TYPES[patient.work_type as keyof typeof WORK_TYPES] || patient.work_type)}
                    >
                      {!patient.work_type ? '[未入力]' : WORK_TYPES[patient.work_type as keyof typeof WORK_TYPES] || patient.work_type}
                    </Typography>
                  </InfoFieldItem>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          {/* がん罹患歴セクション */}
          {cancerHistoriesContent && (
            <Grid item xs={12}>
              {cancerHistoriesContent}
            </Grid>
          )}

          {/* その他疾患情報 */}
          <Grid item xs={12}>
            <SectionWithPaper title="その他、がん以外の慢性疾患" fullWidth>
              <Typography
                variant="body1"
                sx={{
                  ...getUnsetStyle(patient.other_diseases_info || '[未入力]'),
                  whiteSpace: 'pre-line'
                }}
              >
                {patient.other_diseases_info || '[未入力]'}
              </Typography>
            </SectionWithPaper>
          </Grid>

          {/* その他、がん治療歴 */}
          <Grid item xs={12}>
            <SectionWithPaper title="その他、がん治療歴" fullWidth>
              <Typography
                variant="body1"
                sx={{
                  ...getUnsetStyle(patient.cancer_treatment_info || '[未入力]'),
                  whiteSpace: 'pre-line'
                }}
              >
                {patient.cancer_treatment_info || '[未入力]'}
              </Typography>
            </SectionWithPaper>
          </Grid>

          {/* 趣味・関心事 */}
          <Grid item xs={12}>
            <SectionWithPaper title="趣味・関心事" fullWidth>
              <Typography
                variant="body1"
                sx={{
                  ...getUnsetStyle(patient.matter_of_concern || '[未入力]'),
                  whiteSpace: 'pre-line'
                }}
              >
                {patient.matter_of_concern || '[未入力]'}
              </Typography>
            </SectionWithPaper>
          </Grid>

          {/* 連絡先情報 */}
          <Grid item xs={12}>
            <SectionWithPaper title="連絡先情報" fullWidth>
              <InfoFieldItem label="メールアドレス" labelWidth="160px">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <MaskedEmailDisplay email={patient.email}/>
                  <Button
                    onClick={handlePasswordResetClick}
                    size="small"
                    startIcon={<LockResetIcon fontSize="small"/>}
                    sx={{
                      color: 'primary.main',
                      textTransform: 'none',
                      ml: 2
                    }}
                  >
                    パスワードリセット
                  </Button>
                </Box>
              </InfoFieldItem>
              {patient.reset_password_sent_at && passwordResetExpiry && (
                <Box sx={{ mt: 1, ml: '160px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TimeDisplay
                    timestamp={patient.reset_password_sent_at}
                    prefix="パスワードリセット日時"
                    tooltipPrefix="パスワードリセット日時"
                    tooltipPlacement="top"
                    fontSize="0.85rem"
                    color="text.secondary"
                  />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.85rem',
                      color: passwordResetExpiry.isExpired ? 'error.light' : 'text.secondary',
                      ml: 0.5
                    }}
                  >
                    ({passwordResetExpiry.isExpired ? passwordResetExpiry.remainingTime : `有効期限: ${passwordResetExpiry.remainingTime}`})
                  </Typography>
                </Box>
              )}
            </SectionWithPaper>
          </Grid>

          {/* メモ */}
          <Grid item xs={12}>
            <SectionWithPaper
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="h6" component="span">
                    メモ
                  </Typography>
                  <IconButton
                    onClick={handleEditClick}
                    size="small"
                    sx={{ color: 'primary.main' }}
                    title="メモを編集"
                  >
                    <EditIcon fontSize="small"/>
                  </IconButton>
                </Box>
              }
              fullWidth
            >
              <Typography
                variant="body1"
                sx={{
                  ...getUnsetStyle(patient.senior_memo || '[未入力]'),
                  whiteSpace: 'pre-line',
                  fontWeight: patient.senior_memo ? 'normal' : 'normal'
                }}
              >
                {patient.senior_memo || '[未入力]'}
              </Typography>
            </SectionWithPaper>
          </Grid>
        </Grid>
      </CardContent>

      {/* メモ編集ダイアログ */}
      <MemoEditDialog
        open={isEditDialogOpen}
        memo={patient.senior_memo || ''}
        onClose={() => setIsEditDialogOpen(false)}
        onSubmit={handleMemoSubmit}
      />

      {/* パスワードリセットダイアログ */}
      <PasswordResetDialog
        open={isPasswordResetDialogOpen}
        userId={patient.id}
        userName={patient.name}
        userType="patient"
        onClose={() => {
          setIsPasswordResetDialogOpen(false);
          // ダイアログを閉じた後、患者情報を更新してreset_password_sent_atを反映
          if (onPatientUpdate) {
            onPatientUpdate();
          }
        }}
      />

      {/* 最終更新日時と最終ログイン日時 - カードの右下 */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
        {patient.current_sign_in_at ? (
          <TimeDisplay
            timestamp={patient.current_sign_in_at}
            prefix="最終ログイン"
            tooltipPrefix="最終ログイン日時"
            tooltipPlacement="top"
          />
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            最終ログイン: -
          </Typography>
        )}
        <TimeDisplay
          timestamp={patient.updated_at}
          prefix="プロフィール更新"
          tooltipPrefix="プロフィール更新日時"
          tooltipPlacement="top"
        />
      </Box>
    </Card>
  );
};
export default ProfileSection;
